import React, { ReactNode, useContext } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { AppContextProvider } from '../../../../AppLoader';
import { EnforcementCard } from '../../EnforcementCard';
import { ButtonWithLoader } from '../../../../ButtonWithLoader/ButtonWithLoader';
import { ThemedButton } from '../../../../ThemedComponents';
import { AsAddOnSelectDeviceBody } from './AsAddOnSelectDeviceBody';
import { AsAddOnSuccessContent } from './AsAddOnSuccessContent';
import {
  OwnerAppConfirmPendingBody,
  OwnerAppConfirmErrorBody,
} from '../OwnerAppConfirmBody';
import { SendNotificationState } from '../../../../../stores/premiumUsers2FaEnforcement/ownerApp/ownerApp';
import {
  ApprovalChallengeStatus,
  Steps,
} from '../../../../../stores/premiumUsers2FaEnforcement/ownerApp/types';

/** Which AsAddOn steps have been migrated to the wix-2025 card. */
export const REDESIGNED_ASADDON_STEPS: Steps[] = [
  Steps.DeviceSelectionStep,
  Steps.ConfirmationStep,
  Steps.SuccessfulStep,
];

interface StepConfig {
  title: ReactNode;
  subtitle?: ReactNode;
  primaryButton?: ReactNode;
  secondaryButton?: ReactNode;
  showBackButton?: boolean;
  backButtonCallback?: () => void;
  body: ReactNode;
}

/**
 * shell for the redesigned AsAddOn flow (`AddOwnerAppAsAdditionalModal`) — the
 * end-of-enforcement "also add the Wix app" offer. All actions reference the shared `ownerAppStore`.
 */
export const AddOwnerAppAsAdditionalRedesign: React.FC = observer(() => {
  const {
    rootStore: {
      postLoginStore: { premiumUsers2FaEnforcementStore: enforcementStore },
      displayStore,
    },
  } = useContext(AppContextProvider);
  const store = enforcementStore.ownerAppStore;
  const { t } = useTranslation();
  const { preset } = displayStore;

  const isNotifying =
    store.sendNotificationState === SendNotificationState.Notifying;

  const isPending =
    store.authStatus === ApprovalChallengeStatus.PENDING_APPROVAL ||
    store.authStatus === ApprovalChallengeStatus.APPROVED;

  const stepConfigs: Partial<Record<Steps, StepConfig>> = {
    [Steps.DeviceSelectionStep]: {
      title: t('ownerAppAsAddOnMethod.selectDeviceStep.title'),
      subtitle: t('ownerAppAsAddOnMethod.selectDeviceStep.subtitle'),
      primaryButton: (
        <ButtonWithLoader
          skin={preset.buttonDesign?.skin ?? 'standard'}
          showLoader={isNotifying}
          onClick={store.nextStep}
        >
          {t('ownerAppAsAddOnMethod.primary')}
        </ButtonWithLoader>
      ),
      // Opt-out: leave without adding the Wix app add-on.
      secondaryButton: (
        <ThemedButton
          priority="secondary"
          size="large"
          fullWidth
          onClick={() =>
            store.proceedToPostAuthUrl('opt out of add owner app add-on')
          }
          style={{
            ...(preset.buttonDesign?.height && {
              height: displayStore.buttonHeight,
            }),
            ...(displayStore.buttonFontSize && {
              ['--wds-font-size-body-medium' as string]:
                displayStore.buttonFontSize,
            }),
          }}
        >
          {t('ownerAppAsAddOnMethod.secondary')}
        </ThemedButton>
      ),
      body: <AsAddOnSelectDeviceBody />,
    },
    [Steps.ConfirmationStep]: {
      title: t('ownerAppAsAddOnMethod.title'),
      subtitle: t('ownerAppAsAddOnMethod.subtitle'),
      showBackButton: true,
      backButtonCallback: () => store.setActiveStep(Steps.DeviceSelectionStep),
      body: isPending ? (
        <OwnerAppConfirmPendingBody />
      ) : (
        <OwnerAppConfirmErrorBody />
      ),
    },
    [Steps.SuccessfulStep]: {
      title: t('ownerAppAsAddOnMethod.title'),
      subtitle: t('ownerAppAsAddOnMethod.subtitle'),
      body: <AsAddOnSuccessContent />,
    },
  };

  const config = stepConfigs[store.activeStep] ?? null;

  if (!config) {
    return null;
  }

  return (
    <EnforcementCard
      priority="primary"
      title={config.title}
      subtitle={config.subtitle}
      showBackButton={config.showBackButton}
      backButtonCallback={config.backButtonCallback}
      primaryButton={config.primaryButton}
      secondaryButton={config.secondaryButton}
    >
      {config.body}
    </EnforcementCard>
  );
});
